"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiFileText } from "react-icons/fi";
import { HiOutlineCamera } from "react-icons/hi";
import MicrophoneSelect from "@/app/[tenant]/(console)/scribe/playground/[id]/microphone-select";
import SelectTemplateModal from "@/app/[tenant]/(console)/scribe/playground/[id]/select-template-modal";
import AppointmentLocationPrompt from "@/components/location/appointment-location-prompt";
import PatientSearch from "@/components/patient/patient-search";
import AIBadge from "@/components/ui/ai-badge";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioCardCheckedIndicator, RadioCardGroup, RadioCardItem } from "@/components/ui/radio-card-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Tooltip from "@/components/ui/tooltip";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { formatDate, formatName, formatPhoneNumber, formatTime } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { NoteType } from "@/types/enums/note-state";
import { LastNotePrefill, NoteTemplateTableItem } from "@/types/notes";
import { PatientDetail, PatientSummary } from "@/types/patient";
import { appointmentsQueryOptions } from "@/api/provider/appointments";
import { useScribeDevice, useScribeRecording } from "@/stores/scribe-store";
export default function Create(t0) {
  const $ = _c(132);
  const {
    patientId,
    setNoteType,
    noteType,
    setPatientId,
    existingPatient,
    overrideLocationId,
    eventId,
    promptAppointment,
    selectedTemplate,
    setSelectedTemplate,
    suggestedTemplateIds,
    onAppointmentPicked,
    isDialogOpen,
    lastNote,
    fillFromLastNote,
    setFillFromLastNote
  } = t0;
  const {
    selectedDevice,
    micPermissionGranted
  } = useScribeDevice();
  const {
    stopAudio,
    setRecordingId
  } = useScribeRecording();
  const {
    currentUser,
    currentLocationId,
    updatePrimaryLocation
  } = useUser();
  const {
    locations
  } = useLocations();
  const [locationError, setLocationError] = useState();
  const [patientError, setPatientError] = useState();
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(existingPatient);
  const {
    tenant
  } = useTenant();
  const didPrefillPatientRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== isDialogOpen) {
    t1 = () => {
      if (!isDialogOpen) {
        didPrefillPatientRef.current = false;
      }
    };
    t2 = [isDialogOpen];
    $[0] = isDialogOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== existingPatient || $[4] !== isDialogOpen) {
    t3 = () => {
      if (!isDialogOpen || !existingPatient || didPrefillPatientRef.current) {
        return;
      }
      didPrefillPatientRef.current = true;
      setSelectedPatient(existingPatient);
    };
    t4 = [isDialogOpen, existingPatient];
    $[3] = existingPatient;
    $[4] = isDialogOpen;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  tenant?.subDomain;
  let t5;
  if ($[7] !== currentUser) {
    t5 = hasPermission(currentUser, PERMISSIONS.AI_SCRIBE_USE);
    $[7] = currentUser;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const currentUserHasAiScribeUsePermission = t5;
  let t6;
  if ($[9] !== currentUser) {
    t6 = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
    $[9] = currentUser;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const currentUserHasWritePermission = t6;
  let t7;
  if ($[11] !== currentUser) {
    t7 = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_UPLOAD);
    $[11] = currentUser;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const currentUserHasUploadNotesPermission = t7;
  let t8;
  if ($[13] !== currentUser || $[14] !== setRecordingId || $[15] !== stopAudio) {
    t8 = () => {
      stopAudio(currentUser);
      setRecordingId(undefined);
    };
    $[13] = currentUser;
    $[14] = setRecordingId;
    $[15] = stopAudio;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== patientId) {
    t9 = [patientId];
    $[17] = patientId;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  useEffect(t8, t9);
  const isPatientOptedOutOfAi = existingPatient?.aiScribeConsent === false;
  let t10;
  if ($[19] !== currentUserHasAiScribeUsePermission || $[20] !== currentUserHasUploadNotesPermission || $[21] !== currentUserHasWritePermission || $[22] !== isPatientOptedOutOfAi || $[23] !== noteType || $[24] !== setNoteType) {
    t10 = () => {
      const preferredNoteType = currentUserHasWritePermission ? currentUserHasAiScribeUsePermission && !isPatientOptedOutOfAi ? NoteType.AI : NoteType.MANUAL : currentUserHasUploadNotesPermission ? NoteType.UPLOAD : NoteType.MANUAL;
      const isNoteTypeAllowed = noteType === NoteType.AI ? currentUserHasWritePermission && currentUserHasAiScribeUsePermission && !isPatientOptedOutOfAi : noteType === NoteType.MANUAL ? currentUserHasWritePermission : noteType === NoteType.UPLOAD ? currentUserHasUploadNotesPermission : false;
      if (noteType && isNoteTypeAllowed) {
        return;
      }
      setNoteType(preferredNoteType);
    };
    $[19] = currentUserHasAiScribeUsePermission;
    $[20] = currentUserHasUploadNotesPermission;
    $[21] = currentUserHasWritePermission;
    $[22] = isPatientOptedOutOfAi;
    $[23] = noteType;
    $[24] = setNoteType;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== currentUserHasAiScribeUsePermission || $[27] !== currentUserHasUploadNotesPermission || $[28] !== currentUserHasWritePermission || $[29] !== isPatientOptedOutOfAi) {
    t11 = [currentUserHasWritePermission, currentUserHasAiScribeUsePermission, currentUserHasUploadNotesPermission, isPatientOptedOutOfAi];
    $[26] = currentUserHasAiScribeUsePermission;
    $[27] = currentUserHasUploadNotesPermission;
    $[28] = currentUserHasWritePermission;
    $[29] = isPatientOptedOutOfAi;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  useEffect(t10, t11);
  let t12;
  if ($[31] !== currentUser?.id) {
    t12 = appointmentsQueryOptions.inWindow({
      providerId: currentUser?.id || ""
    });
    $[31] = currentUser?.id;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = !!currentUser?.id;
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = {
      ...t12,
      enabled: t13
    };
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const {
    data: patientsInWindow
  } = useQuery(t14);
  const isDialogPatient = !patientId || !existingPatient || patientId === existingPatient.id;
  let t15;
  if ($[36] !== patientId || $[37] !== patientsInWindow) {
    t15 = new Set((patientsInWindow?.find(t16 => {
      const {
        patient
      } = t16;
      return patient.id === patientId;
    })?.appointments ?? []).map(_temp).filter(_temp2));
    $[36] = patientId;
    $[37] = patientsInWindow;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const windowLocationIds = t15;
  let t16;
  if ($[39] !== eventId || $[40] !== isDialogPatient || $[41] !== overrideLocationId || $[42] !== promptAppointment) {
    t16 = !isDialogPatient ? undefined : overrideLocationId ? {
      id: eventId,
      locationId: overrideLocationId
    } : promptAppointment ?? undefined;
    $[39] = eventId;
    $[40] = isDialogPatient;
    $[41] = overrideLocationId;
    $[42] = promptAppointment;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  const dialogAppointment = t16;
  let t17;
  if ($[44] !== dialogAppointment || $[45] !== windowLocationIds) {
    t17 = dialogAppointment ? dialogAppointment.locationId ?? undefined : windowLocationIds.size === 1 ? [...windowLocationIds][0] : undefined;
    $[44] = dialogAppointment;
    $[45] = windowLocationIds;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const appointmentLocationId = t17;
  let t18;
  if ($[47] !== updatePrimaryLocation) {
    t18 = async locationId => {
      setLocationError(undefined);
      await updatePrimaryLocation(locationId);
    };
    $[47] = updatePrimaryLocation;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const handleLocationChange = t18;
  let t19;
  if ($[49] !== setNoteType) {
    t19 = value => {
      if (value) {
        setNoteType(value);
      }
    };
    $[49] = setNoteType;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const handleNoteTypeChange = t19;
  let t20;
  if ($[51] !== currentUserHasAiScribeUsePermission || $[52] !== currentUserHasWritePermission || $[53] !== isPatientOptedOutOfAi || $[54] !== micPermissionGranted || $[55] !== selectedDevice) {
    t20 = () => {
      if (isPatientOptedOutOfAi) {
        return "Patient has opted out of AI Scribe";
      }
      if (!currentUserHasWritePermission) {
        return "You don't have permission to create notes";
      }
      if (!currentUserHasAiScribeUsePermission) {
        return "You don't have permission to use AI Scribe";
      }
      if (!micPermissionGranted) {
        return "Microphone permission required for AI Scribe";
      }
      if (!selectedDevice) {
        return "Please select a microphone device";
      }
      return null;
    };
    $[51] = currentUserHasAiScribeUsePermission;
    $[52] = currentUserHasWritePermission;
    $[53] = isPatientOptedOutOfAi;
    $[54] = micPermissionGranted;
    $[55] = selectedDevice;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const getAiScribeDisabledReason = t20;
  let t21;
  if ($[57] !== currentUserHasWritePermission) {
    t21 = () => {
      if (!currentUserHasWritePermission) {
        return "You don't have permission to create notes";
      }
      return null;
    };
    $[57] = currentUserHasWritePermission;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const getManualDisabledReason = t21;
  let t22;
  if ($[59] !== currentUserHasUploadNotesPermission) {
    t22 = () => {
      if (!currentUserHasUploadNotesPermission) {
        return "You don't have permission to upload notes";
      }
      return null;
    };
    $[59] = currentUserHasUploadNotesPermission;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  const getUploadDisabledReason = t22;
  const isAiScribeDisabled = isPatientOptedOutOfAi || !currentUserHasWritePermission || !currentUserHasAiScribeUsePermission || !micPermissionGranted || !selectedDevice;
  const isManualDisabled = !currentUserHasWritePermission;
  const isUploadDisabled = !currentUserHasUploadNotesPermission;
  let t23;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Label htmlFor="note-type">Note Type</Label>;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== handleNoteTypeChange) {
    t24 = value_0 => handleNoteTypeChange(value_0 as NoteType);
    $[62] = handleNoteTypeChange;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== getAiScribeDisabledReason || $[65] !== isAiScribeDisabled) {
    t25 = isAiScribeDisabled ? <Tooltip content={getAiScribeDisabledReason()}><RadioCardItem value={NoteType.AI} disabled={isAiScribeDisabled} className={cx("px-3 py-3", isAiScribeDisabled ? "cursor-not-allowed" : "")} icon={<AIBadge size={20} className="mr-0" />} title="AI Scribe"><RadioCardCheckedIndicator /></RadioCardItem></Tooltip> : <RadioCardItem value={NoteType.AI} disabled={isAiScribeDisabled} className="text-nowrap px-3 py-3" icon={<AIBadge size={20} className="mr-0" />} title="AI Scribe"><RadioCardCheckedIndicator /></RadioCardItem>;
    $[64] = getAiScribeDisabledReason;
    $[65] = isAiScribeDisabled;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== getManualDisabledReason || $[68] !== isManualDisabled) {
    t26 = isManualDisabled ? <Tooltip content={getManualDisabledReason()}><RadioCardItem value={NoteType.MANUAL} title="Manual" className="px-3 py-3" disabled={isManualDisabled} icon={<FiFileText size={20} className="text-muted-foreground" />}><RadioCardCheckedIndicator /></RadioCardItem></Tooltip> : <RadioCardItem value={NoteType.MANUAL} title="Manual" className="px-3 py-3" icon={<FiFileText size={20} className="text-muted-foreground" />}><RadioCardCheckedIndicator /></RadioCardItem>;
    $[67] = getManualDisabledReason;
    $[68] = isManualDisabled;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== getUploadDisabledReason || $[71] !== isUploadDisabled) {
    t27 = isUploadDisabled ? <Tooltip content={getUploadDisabledReason()}><RadioCardItem value={NoteType.UPLOAD} title="Upload" className="px-3 py-3" icon={<HiOutlineCamera size={20} className="text-muted-foreground" />} disabled={isUploadDisabled}><RadioCardCheckedIndicator /></RadioCardItem></Tooltip> : <RadioCardItem value={NoteType.UPLOAD} title="Upload" className="px-3 py-3" icon={<HiOutlineCamera size={20} className="text-muted-foreground" />} disabled={isUploadDisabled}><RadioCardCheckedIndicator /></RadioCardItem>;
    $[70] = getUploadDisabledReason;
    $[71] = isUploadDisabled;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== noteType || $[74] !== t24 || $[75] !== t25 || $[76] !== t26 || $[77] !== t27) {
    t28 = <div className="space-y-2">{t23}<RadioCardGroup value={noteType} onValueChange={t24} className="grid grid-cols-3 md:gap-3">{t25}{t26}{t27}</RadioCardGroup></div>;
    $[73] = noteType;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== currentUserHasAiScribeUsePermission || $[80] !== noteType) {
    t29 = currentUserHasAiScribeUsePermission && noteType === NoteType.AI && <MicrophoneSelect />;
    $[79] = currentUserHasAiScribeUsePermission;
    $[80] = noteType;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== isTemplateModalOpen || $[83] !== noteType || $[84] !== selectedTemplate || $[85] !== setSelectedTemplate || $[86] !== suggestedTemplateIds) {
    t30 = noteType === NoteType.MANUAL && <div className="space-y-1"><Label htmlFor="template" mandatory={true}>Template</Label><div><Button variant="secondary" onClick={() => setIsTemplateModalOpen(true)} className={cx("w-full max-w-md justify-start text-left font-normal", !selectedTemplate && "text-muted-foreground")}>{selectedTemplate?.name || "Select a template"}</Button>{!selectedTemplate && <p className="mt-1 text-caption text-status-error-text">Please select a template</p>}</div><p className="text-caption text-foreground-secondary">A template is required to create a manual note</p><SelectTemplateModal isOpen={isTemplateModalOpen} setIsOpen={setIsTemplateModalOpen} onTemplateSelected={template => {
        setSelectedTemplate?.(template);
        setIsTemplateModalOpen(false);
      }} description="Choose a template for your manual note." needsPlaceholderUrls={false} suggestedTemplateIds={suggestedTemplateIds} closable={true} /></div>;
    $[82] = isTemplateModalOpen;
    $[83] = noteType;
    $[84] = selectedTemplate;
    $[85] = setSelectedTemplate;
    $[86] = suggestedTemplateIds;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== fillFromLastNote || $[89] !== lastNote || $[90] !== noteType || $[91] !== selectedTemplate || $[92] !== setFillFromLastNote) {
    t31 = noteType === NoteType.MANUAL && selectedTemplate && lastNote && setFillFromLastNote && <label className="flex items-start gap-2"><Checkbox checked={fillFromLastNote} onCheckedChange={checked => setFillFromLastNote(checked === true)} /><span className="space-y-0.5"><span className="block text-body-strong text-foreground">Fill in from last note</span><span className="block text-caption text-foreground-secondary">Copies answers from this patient's{" "}{selectedTemplate.name} note on{" "}{formatDate(lastNote.visitDate)}. Signatures, vitals, and attachments start blank.</span></span></label>;
    $[88] = fillFromLastNote;
    $[89] = lastNote;
    $[90] = noteType;
    $[91] = selectedTemplate;
    $[92] = setFillFromLastNote;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  let t32;
  if ($[94] !== currentLocationId || $[95] !== handleLocationChange || $[96] !== locationError || $[97] !== locations || $[98] !== overrideLocationId || $[99] !== tenant?.enabledModules) {
    t32 = locations.length > 0 && !overrideLocationId && <div className="space-y-1"><Label htmlFor="location" mandatory={tenant?.enabledModules?.includes("LOCATIONS")}>Location</Label><Select value={currentLocationId || ""} onValueChange={value_1 => handleLocationChange(value_1)}><SelectTrigger className="max-w-md" hasError={!!locationError} errorMessage={locationError}><SelectValue placeholder="Select location" /></SelectTrigger><SelectContent>{locations.map(_temp3)}</SelectContent></Select></div>;
    $[94] = currentLocationId;
    $[95] = handleLocationChange;
    $[96] = locationError;
    $[97] = locations;
    $[98] = overrideLocationId;
    $[99] = tenant?.enabledModules;
    $[100] = t32;
  } else {
    t32 = $[100];
  }
  const t33 = dialogAppointment?.id;
  let t34;
  if ($[101] !== appointmentLocationId || $[102] !== patientId || $[103] !== t33) {
    t34 = <AppointmentLocationPrompt key={patientId} locationId={appointmentLocationId} appointmentId={t33} />;
    $[101] = appointmentLocationId;
    $[102] = patientId;
    $[103] = t33;
    $[104] = t34;
  } else {
    t34 = $[104];
  }
  let t35;
  if ($[105] !== onAppointmentPicked || $[106] !== patientId || $[107] !== patientsInWindow || $[108] !== setPatientId || $[109] !== tenant?.enabledModules) {
    t35 = tenant?.enabledModules?.includes("SCHEDULING") && patientsInWindow && patientsInWindow.length > 0 && <div><Label htmlFor="appointment-patients">Choose a patient from your appointments</Label><p className="mb-3 text-body text-muted-foreground">Showing patients with appointments from the last 30 minutes to the next hour</p><div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{patientsInWindow.map(t36 => {
          const {
            patient: patient_0,
            appointments
          } = t36;
          const primary = appointments[0];
          if (!primary) {
            return null;
          }
          const appointmentTemplateIds = appointments.flatMap(_temp4).filter(_temp5);
          return <button key={patient_0.id} onClick={() => {
            didPrefillPatientRef.current = true;
            setPatientId(patient_0.id);
            setSelectedPatient({
              id: patient_0.id,
              firstName: patient_0.firstName,
              lastName: patient_0.lastName
            } as PatientSummary);
            setPatientError(undefined);
            onAppointmentPicked?.(patient_0.id, appointmentTemplateIds);
          }} className={cx("rounded-lg border p-3 text-left hover:border-border hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2", patientId === patient_0.id && "border-ring ring-1 ring-ring")}><div className="space-y-1.5"><div><Label>{formatName(patient_0.firstName, patient_0.lastName)}</Label><div className="mt-0.5 flex flex-col text-caption text-foreground-secondary">{patient_0.dateOfBirth && <span className="truncate">DOB: {formatDate(patient_0.dateOfBirth)}</span>}{patient_0.phoneNumber && <span className="flex-shrink-0 whitespace-nowrap">{formatPhoneNumber(patient_0.phoneNumber)}</span>}</div></div><div className="border-t pt-1.5"><p className="whitespace-nowrap text-caption-strong leading-tight text-foreground">{formatTime(primary.appointmentStart)} -{" "}{formatTime(primary.appointmentEnd)}</p><p className="truncate text-caption leading-tight text-foreground-secondary">{primary.appointmentType || primary.appointmentTitle || "Appointment"}</p></div></div></button>;
        })}</div></div>;
    $[105] = onAppointmentPicked;
    $[106] = patientId;
    $[107] = patientsInWindow;
    $[108] = setPatientId;
    $[109] = tenant?.enabledModules;
    $[110] = t35;
  } else {
    t35 = $[110];
  }
  const t36 = tenant?.enabledModules?.includes("SCHEDULING") && patientsInWindow && patientsInWindow.length > 0 ? "Or search for any patient" : "Patient";
  let t37;
  if ($[111] !== t36) {
    t37 = <Label htmlFor="patient">{t36}</Label>;
    $[111] = t36;
    $[112] = t37;
  } else {
    t37 = $[112];
  }
  let t38;
  if ($[113] !== setPatientId) {
    t38 = patient_1 => {
      didPrefillPatientRef.current = true;
      setSelectedPatient(patient_1);
      setPatientId(patient_1?.id || "");
      setPatientError(undefined);
    };
    $[113] = setPatientId;
    $[114] = t38;
  } else {
    t38 = $[114];
  }
  const t39 = !!patientError;
  let t40;
  if ($[115] !== patientError || $[116] !== selectedPatient || $[117] !== t38 || $[118] !== t39) {
    t40 = <PatientSearch patient={selectedPatient} onPatientSelect={t38} placeholder="Search patient by phone, id, name or dob" className="w-full max-w-md" hasError={t39} errorMessage={patientError} />;
    $[115] = patientError;
    $[116] = selectedPatient;
    $[117] = t38;
    $[118] = t39;
    $[119] = t40;
  } else {
    t40 = $[119];
  }
  let t41;
  if ($[120] !== t37 || $[121] !== t40) {
    t41 = <div className="space-y-1">{t37}{t40}</div>;
    $[120] = t37;
    $[121] = t40;
    $[122] = t41;
  } else {
    t41 = $[122];
  }
  let t42;
  if ($[123] !== t28 || $[124] !== t29 || $[125] !== t30 || $[126] !== t31 || $[127] !== t32 || $[128] !== t34 || $[129] !== t35 || $[130] !== t41) {
    t42 = <div className="min-h-full w-full overflow-y-auto p-1"><div className="max-w-xl space-y-8"><div className="space-y-6">{t28}{t29}{t30}{t31}{t32}{t34}{t35}{t41}</div></div></div>;
    $[123] = t28;
    $[124] = t29;
    $[125] = t30;
    $[126] = t31;
    $[127] = t32;
    $[128] = t34;
    $[129] = t35;
    $[130] = t41;
    $[131] = t42;
  } else {
    t42 = $[131];
  }
  return t42;
}
function _temp5(id_0, i, ids) {
  return ids.indexOf(id_0) === i;
}
function _temp4(a) {
  return a.noteTemplateIds ?? [];
}
function _temp3(location) {
  return <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>;
}
function _temp2(id) {
  return !!id;
}
function _temp(appointment) {
  return appointment.location?.id;
}